import {setOnboardingInfo} from "../../../redux/slices/onboarding";
import {setCreateOrganizationData, setOrganizationUpdateSuccess} from "../../../redux/slices/organization";
import React, {useEffect, useState} from "react";
import toast from "react-hot-toast";
import DeletableLabel from "./components/DeletableLabel";
import {SignOutButton, useClerk, useOrganizationList} from "@clerk/clerk-react";
import useOrganizationService from '../../../services/organization';
import {useJwt} from "../../../helpers/common";
import {useNavigate} from "react-router-dom";
import {useDispatch, useSelector} from "react-redux";

const companyTypeData = [
  {label: 'Construction', value:3},
  {label: 'Professional Services - Software', value: 2},
  {label: 'Software Product Development', value: 1},
]

const SignUpForm = () => {
  const JWTdata = useJwt()
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [picture, setPicture] = useState<{ picturePreview?: string; pictureAsFile?: File }>({});
  const [teamMemberEmail,setTeamMemberEmail] = useState("")
  const [teamMembersEmailList,setTeamMembersEmailList] = useState<string[]>([])
  const [businessType, setBusinessType] = useState('')
  const { createOrg } = useOrganizationService();
  const { createOrganization, setActive } = useOrganizationList();
  const {orgUpdateLoading, loading, orgdata} = useSelector((state: any) => state.organization);
  const onboardingData = useSelector((state: any) => state.onboarding);

  const uploadPicture = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPicture({
      /* contains the preview, if you want to show the picture to the user
           you can access it with this.state.currentPicture
       */
      picturePreview: e.target.files && e.target.files[0] ? URL.createObjectURL(e.target.files[0]) : undefined,
      /* this contains the file we want to send */
      pictureAsFile: e.target.files && e.target.files[0] ? e.target.files[0] : undefined,
    });
  };

  async function postData() {
    if(!onboardingData.company.name) {
      toast.error("Company Name is Mandatory", {duration: 1000})
      return
    }
    if(!businessType) {
      toast.error("Business Type is Mandatory", {duration: 1000})
      return
    }

    if(typeof createOrganization == "function" && !orgUpdateLoading){
      try {

        const formData = new FormData();
        if(picture?.pictureAsFile){
          formData.append("file", picture?.pictureAsFile);
        }
        formData.append("name", onboardingData.company.name);
        formData.append("business_type", businessType);
        formData.append("invite_users", teamMembersEmailList.toString());
        formData.append("user_id", (JWTdata as any).user_id);

        await createOrg(formData as any);
      } catch (error) {
        console.error("Error creating organization:", error);
        toast.error("Error creating organization. Please try again.");
      }
    }
  }

  useEffect(() => {
    if (orgdata?.org_id && !loading) {
      let attempts = 0;
      const maxAttempts = 5;

      const trySetActive = async () => {
        try {
          if (attempts >= maxAttempts) {
            console.error("Max attempts reached for setting active organization");
            return;
          }

          if (typeof setActive === "function") {
            await setActive({ organization: orgdata.org_id });
            dispatch(setCreateOrganizationData({}));
            dispatch(setOrganizationUpdateSuccess(false));
            window.location.href = "/workitems";
          } else {
            attempts++;
            setTimeout(trySetActive, 1000);
          }
        } catch (error) {
          console.error("Error setting active organization:", error);
          attempts++;
          setTimeout(trySetActive, 1000);
        }
      };

      trySetActive();
    }
  }, [orgdata, loading, setActive, navigate]);

  const handleAddEmail = (event: React.KeyboardEvent) => {
    if(event.key === 'Enter' && teamMemberEmail){
      if(!validateEmail(teamMemberEmail)) {
        toast.error("please enter valid email", {duration: 1000})
        return
      }
      const copy = [...teamMembersEmailList, teamMemberEmail]
      const uniqueList = Array.from(new Set(copy))
      setTeamMembersEmailList([...uniqueList])
      setTeamMemberEmail('')
    }
  }

  const handleFocusEmail = (event: React.FocusEvent<HTMLInputElement>) => {
    if(!teamMemberEmail){
      return
    }
    if(!validateEmail(teamMemberEmail)) {
      toast.error("please enter valid email", {duration: 1000})
      return
    }
    const copy = [...teamMembersEmailList, teamMemberEmail]
    const uniqueList = Array.from(new Set(copy))
    setTeamMembersEmailList([...uniqueList])
    setTeamMemberEmail('')
  }

  const handleDeleteEmail = (index: number) => {
    const copy = teamMembersEmailList
    copy.splice(index, 1)
    setTeamMembersEmailList([...copy])
  }

  const validateEmail = (email: string) => {
    //RFC 2822 standard email validation https://www.w3resource.com/javascript/form/email-validation.php
    const emailReg = /(?:[a-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*|"(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21\x23-\x5b\x5d-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])*")@(?:(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?|\[(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?|[a-z0-9-]*[a-z0-9]:(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21-\x5a\x53-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])+)\])/
    return emailReg.test(email)
  }

  const handleLoginWithExistingAccount = () => {
    try {
      localStorage.clear();
      sessionStorage.clear();
      document.cookie.split(";").forEach((cookie) => {
        document.cookie = cookie
          .replace(/^ +/, "")
          .replace(/=.*/, "=;expires=" + new Date(0).toUTCString() + ";path=/");
      });
    } catch (e) {
      console.error("Error clearing storage:", e);
    }

    window.location.replace('/');
  }

  return (
    <div className={`relative w-full pt-10 pb-16 bg-black`}>
      <div className={`mx-auto px-4 xs:px-5 relative min-h-full`}>
        <div className="pointer-events-none absolute -top-6 left-2 w-72 h-48 opacity-40 bg-gradient-to-tr from-blue-700 via-indigo-800 to-blue-700 rounded-2xl blur-3xl" />
        <div className="pointer-events-none absolute -bottom-10 right-0 w-72 h-48 opacity-30 bg-gradient-to-tr from-blue-700 via-indigo-800 to-blue-700 rounded-2xl blur-3xl" />
        <div className="flex flex-col items-center relative z-10">
          <div className="w-full max-w-lg xs:max-w-full xs:w-full">
            <div className="relative">
              <div className="pointer-events-none absolute -inset-3 rounded-3xl bg-black/60 blur-2xl" />
              {(orgUpdateLoading || loading) && (
                <div className="absolute inset-0 z-20 rounded-3xl bg-black/40 backdrop-blur-sm flex items-center justify-center">
                  <div className="flex flex-col items-center gap-2">
                    <div className="h-8 w-8 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    <span className="text-white text-sm">Creating account...</span>
                  </div>
                </div>
              )}
              {/* <div className="relative bg-light-surface dark:bg-tertiary-dark border border-light-border-default dark:border-gray-700 rounded-2xl shadow-xl shadow-black/40 p-5 xs:p-4 flex flex-col gap-4"> */}
              <div className="relative bg-tertiary-dark border border-gray-700 rounded-2xl p-5 xs:p-4 flex flex-col gap-4">
                <div className="mb-2">
                  <h1 className="text-light-text-inverse text-2xl font-semibold">Complete Sign up</h1>
                  <p className="mt-1 text-gray-400 text-sm">Tell us about your company and invite teammates. </p>
                </div>
                <div className="w-full">
                  <label htmlFor="CompanyName" className="block mb-2 text-sm font-medium text-white">Company Name*</label>
                  <input
                    type="text"
                    id="CompanyName"
                    className="w-full rounded-lg border border-gray-600 bg-gray-700 text-white text-sm px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-light focus:border-primary-light"
                    onChange={(e) => { dispatch(setOnboardingInfo({ name: e.target.value })) }}
                    name="company"
                    placeholder="Enter your company name"
                    defaultValue={onboardingData.company.name}
                  />
                </div>

                <div className="w-full">
                  <label htmlFor="BusinessType" className="block mb-2 text-sm font-medium text-white">Business Type*</label>
                  <select
                    id="BusinessType"
                    className="w-full rounded-lg border border-gray-600 bg-gray-700 text-white text-sm px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-light focus:border-primary-light"
                    value={businessType}
                    onChange={(e)=>{setBusinessType(e.target.value)}}
                  >
                    <option value={''}>Select business type</option>
                    {companyTypeData && companyTypeData.map(({label, value}) => (
                      <option value={value} key={label}>{label}</option>
                    ))}
                  </select>
                </div>

                <div className="w-full">
                  <label htmlFor="UploadProfile" className="block mb-2 text-sm font-medium text-white">Upload logo</label>
                  <input
                    className="block w-full text-sm text-gray-300 border border-gray-600 rounded-lg cursor-pointer bg-gray-700 focus:outline-none"
                    aria-describedby="UploadProfileHelp" id="UploadProfile" type="file" accept="image/png, image/jpeg"
                    onChange={uploadPicture}
                  />
                  <p className="mt-1 text-xs text-gray-400" id="UploadProfileHelp">Max Size 12mb (png & jpeg only)</p>
                </div>

                <div className="w-full">
                  <label htmlFor="InviteTeamMembers" className="block mb-2 text-sm font-medium text-white">Invite Team Members</label>
                  <input
                    type="email"
                    id="InviteTeamMembers"
                    className="w-full rounded-lg border border-gray-600 bg-gray-700 text-white text-sm px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-light focus:border-primary-light"
                    onChange={(e) => { setTeamMemberEmail(e.target.value) }}
                    onBlur={(e) => handleFocusEmail(e)}
                    onKeyDown={(e) => handleAddEmail(e)}
                    name="InviteTeamMembers"
                    placeholder="Enter teammate email and press Enter"
                    value={teamMemberEmail}
                  />
                </div>

                {teamMembersEmailList.length > 0 ? (
                  <div className="w-full">
                    <div className="w-full p-3 bg-gray-800/60 rounded-lg border border-gray-700 flex flex-row flex-wrap gap-2">
                      {teamMembersEmailList.map((it, i) => (
                        <DeletableLabel key={it} value={it} onDelete={() => { handleDeleteEmail(i) }}/>
                      ))}
                    </div>
                  </div>
                ) : null}

                <div className="pt-4">
                  <div className="text-white text-base font-semibold">Integrations</div>
                  <div className="text-gray-400 text-sm">Log in and seamlessly integrate your existing tools with Cadence</div>
                  <div className={`flex items-center mt-4 mb-2 justify-center gap-5`}>
                    <div className={`w-28 h-10   p-4 relative bg-white rounded-md flex items-center justify-center`}>
                      <img className="w-[60%]" alt="" src={`https://cadence-fe-bucket.s3.us-east-2.amazonaws.com/images/jiralogo.png?${Date.now()}`} />
                    </div>
                    <div className={`w-28 h-10   p-4  relative bg-white rounded-md flex items-center justify-center`}>
                      <img className="w-[60%]" alt="" src={`https://cadence-fe-bucket.s3.us-east-2.amazonaws.com/images/asana.png?${Date.now()}`} />
                    </div>
                    <div className={`w-28 h-10   p-4 relative bg-white rounded-md flex items-center justify-center`}>
                      <img className="w-[80%]" alt="" src={`https://cadence-fe-bucket.s3.us-east-2.amazonaws.com/images/devopsAsure.png?${Date.now()}`} />
                    </div>
                    <div className={`w-28 h-10   p-4 relative bg-white rounded-md flex items-center justify-center`}>
                      <img className="" alt="" src={`https://cadence-fe-bucket.s3.us-east-2.amazonaws.com/images/mondaylogo.png?${Date.now()}`} />
                    </div>
                  </div>
                </div>

                <div className="flex flex-col gap-3 pt-2">
                  <button
                    className={`w-full px-5 py-2.5 ${loading ? "bg-primary-light/70 cursor-not-allowed" : "bg-primary-light hover:bg-primary-hover"} rounded-lg text-white text-sm font-medium`}
                    onClick={() => { postData() }}
                    disabled={loading}
                  >
                    Create new account
                  </button>

                  <SignOutButton>
                    <button
                      onClick={handleLoginWithExistingAccount}
                      disabled={loading}
                      className="w-full px-3 py-2.5 hover:bg-secondary-dark text-white  hover:text-white transition-colors text-sm rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-light/30"
                    >
                      Login with existing account
                    </button>
                  </SignOutButton>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default SignUpForm